import React from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetNaceDivisions } from "@api";
import { Autocomplete, TextField } from "@components/base";
import { useUserStore } from "@store/user";

interface NaceDivisionProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  naceSectionId: string;
  isDisabled?: boolean;
}

const NaceDivision = <TFieldValues extends FieldValues>({
  control,
  naceSectionId,
  isDisabled,
}: NaceDivisionProps<TFieldValues>): JSX.Element | null => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { naceDivisions, naceDivisionsEnabled } = useGetNaceDivisions(user, {
    naceSectionId,
  });

  if (!naceDivisionsEnabled) {
    return null;
  }

  return (
    <Controller
      control={control}
      name={"naceDivisionId" as Path<TFieldValues>}
      disabled={isDisabled}
      render={({ field }): React.ReactElement => (
        <Autocomplete
          options={naceDivisions ?? []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={
            naceDivisions?.find((d) => d.id === field.value) ?? null
          }
          onChange={(_, option) => field.onChange(option?.id ?? "")}
          disabled={field.disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("entitiesManagement.entityEditor.naceDivision")}
            />
          )}
        />
      )}
    />
  );
};

export default NaceDivision;
